import { randomUUID } from "node:crypto";
import { PNC_CLIENT_HEADER, PNC_CLIENT_WEB } from "@pushback-and-chaos/contracts";
import { ProblemJsonError, throwIfAuthProblem, throwIfProblem } from "./errors.js";
/** Must match apps/api/src/auth/session-cookie.ts. */
const SESSION_COOKIE_NAME = "pc_session";
import { parseSseStream } from "./sse-parse.js";
export class PushbackClient {
    config;
    /** Node-only cookie jar; see the class doc. */
    sessionCookie;
    constructor(config) {
        this.config = config;
    }
    identityHeaders() {
        return this.sessionCookie === undefined ? {} : { cookie: this.sessionCookie };
    }
    /** Captures the session cookie under Node. In a browser getSetCookie() is empty by design. */
    captureSessionCookie(response) {
        const setCookies = typeof response.headers.getSetCookie === "function" ? response.headers.getSetCookie() : [];
        for (const raw of setCookies) {
            const pair = raw.split(";", 1)[0];
            if (pair !== undefined && pair.startsWith(`${SESSION_COOKIE_NAME}=`))
                this.sessionCookie = pair;
        }
    }
    /**
     * `auth: true` for better-auth's own routes (/auth/*): their refusals are
     * read by `throwIfAuthProblem`, which keeps better-auth's code.
     */
    async request(method, path, options = {}) {
        const response = await fetch(`${this.config.baseUrl}${path}`, {
            method,
            credentials: "include",
            headers: {
                ...(options.body !== undefined ? { "content-type": "application/json" } : {}),
                ...this.identityHeaders(),
                ...options.headers,
            },
            ...(options.body !== undefined ? { body: JSON.stringify(options.body) } : {}),
            ...(options.signal !== undefined ? { signal: options.signal } : {}),
        });
        this.captureSessionCookie(response);
        await this.told(options.auth === true ? throwIfAuthProblem(response) : throwIfProblem(response));
        return response;
    }
    /** Awaits a response's check, telling `onProblem` of a refusal before it is rethrown. */
    async told(check) {
        try {
            await check;
        }
        catch (error) {
            if (error instanceof ProblemJsonError)
                this.config.onProblem?.(error);
            throw error;
        }
    }
    async json(method, path, options = {}) {
        const response = await this.request(method, path, options);
        return (await response.json());
    }
    async createGuest(options = {}) {
        // The identity that matters arrives as the Set-Cookie on this response.
        // Without `fresh`, a browser already holding a live guest keeps it.
        return this.json("POST", "/auth/guest", { body: options.fresh === true ? { fresh: true } : {} });
    }
    /**
     * better-auth's endpoints, mounted at /auth/*. Sign-up and sign-in issue its
     * session cookie; work done as a guest is migrated onto the new account on the
     * next authenticated request (apps/api/src/auth/identity.ts).
     *
     * `Origin` is set explicitly because better-auth rejects a state-changing
     * request whose origin it does not recognise — a browser sets this for free,
     * Node's fetch does not, and this class runs in both.
     */
    authBody(body) {
        return { body, headers: { origin: this.config.origin ?? this.config.baseUrl }, auth: true };
    }
    async signUpWithEmail(input) {
        return this.json("POST", "/auth/sign-up/email", this.authBody({ ...input }));
    }
    async signInWithEmail(input) {
        return this.json("POST", "/auth/sign-in/email", this.authBody({ ...input }));
    }
    /** Returns the provider's consent URL for the browser to visit. */
    async socialSignInUrl(provider, callbackURL) {
        const result = await this.json("POST", "/auth/sign-in/social", this.authBody({ provider, callbackURL }));
        return result.url;
    }
    async signOut() {
        await this.request("POST", "/auth/sign-out", this.authBody({}));
        this.sessionCookie = undefined;
    }
    /**
     * THE ACCOUNT EMAILS' DOORS (2026-10-22: hard verification and password
     * reset). Each is better-auth's own route, read by the auth reader so a
     * refusal keeps its code. None sends a callbackURL or redirectTo: the api
     * builds each email's link itself from better-auth's token, to the web's
     * own pages (`#/verify?token=`, `#/reset?token=`), so a token never
     * reaches a server log, a Referer or better-auth's redirect.
     *
     * "Forgot password": better-auth 1.7.2's route is /request-password-reset
     * (there is no /forget-password). It answers the same whether or not the
     * address has an account.
     */
    async requestPasswordReset(email) {
        const answer = await this.json("POST", "/auth/request-password-reset", this.authBody({ email }));
        return { status: answer.status };
    }
    /** The reset page's one request: the token from the link, in the body, and the new password. The token works once. */
    async resetPassword(token, newPassword) {
        const answer = await this.json("POST", "/auth/reset-password", this.authBody({ token, newPassword }));
        return { status: answer.status };
    }
    /** "send another link.": signed out, better-auth answers the same whether or not the address waits to be confirmed. */
    async sendVerificationEmail(email) {
        const answer = await this.json("POST", "/auth/send-verification-email", this.authBody({ email }));
        return { status: answer.status };
    }
    /**
     * The verify page's button: GET /auth/verify-email with the link's token
     * and no callbackURL, so better-auth answers JSON rather than redirecting.
     * A first verification signs this browser in (`autoSignInAfterVerification`)
     * by the cookie on this answer; the guest claim runs on the next request.
     * An address already verified answers the same and signs nobody in, so the
     * caller asks who this is next. A GET, but only a button press sends it.
     *
     * It carries contracts' PNC_CLIENT_HEADER (`x-pnc-client: web`, the review
     * of 2026-10-22): the api refuses a verification without it, because a
     * link to this route opened from another site (a navigation, which can
     * carry no custom header) would otherwise sign the opener's browser in to
     * the link's account and hand it their guest work.
     */
    async verifyEmail(token) {
        const answer = await this.json("GET", `/auth/verify-email?token=${encodeURIComponent(token)}`, { auth: true, headers: { [PNC_CLIENT_HEADER]: PNC_CLIENT_WEB } });
        return { status: answer.status };
    }
    /**
     * The re-accept paper (2026-10-22): records that this account accepts the
     * current terms. No version is sent; the server stamps its own
     * TERMS_VERSION. The answer's body is not read, so the caller asks who this
     * is again for `termsCurrent`.
     */
    async acceptTerms() {
        await this.request("POST", "/me/terms", { body: {} });
    }
    /** Who the server takes this browser to be; `kind: "none"` rather than a 401 when nobody is. */
    async getIdentity() {
        return this.json("GET", "/me/identity");
    }
    /**
     * "Log out." Both halves, because either alone leaves the learner signed in:
     * better-auth's session, and the app's own cookie, which still resolves to
     * the account once a guest has been claimed. better-auth answers 200 when
     * there is no session to end, so a refusal from it is real and is thrown:
     * the learner must not be told they are out while the account is still in.
     */
    async logOut() {
        await this.signOut();
        await this.request("DELETE", "/auth/guest");
        this.sessionCookie = undefined;
    }
    /**
     * The intake (onboarding pass, 2026-09-13): the first call opens it with the
     * learner's prose; every later call on the same intake is the answer to the
     * question the coach asked, or a correction to the read-back.
     */
    async extractProfile(onboardingProse, intakeId) {
        return this.json("POST", "/profile/extract", { body: intakeId === undefined ? { onboardingProse } : { onboardingProse, intakeId } });
    }
    /** A closed-set answer, applied with no model call. */
    async answerProfile(input) {
        return this.json("POST", "/profile/answer", { body: input });
    }
    /** Keeps the profile; the coach lays the first papers on the desk and says what they are (AJ intake, 2026-09-24). */
    async confirmProfile(learnerProfileId) {
        return this.json("POST", "/profile/confirm", { body: { learnerProfileId } });
    }
    /** The open intake to pick up after a reload, or null. */
    async getIntake() {
        return this.json("GET", "/profile/intake");
    }
    /** The one rehearsal move before the first case, and its authored reflection. */
    async rehearse(learnerProfileId, text) {
        return this.json("POST", "/profile/rehearsal", { body: { learnerProfileId, text } });
    }
    async skipRehearsal(learnerProfileId) {
        return this.json("POST", "/profile/rehearsal/skip", { body: { learnerProfileId } });
    }
    async listTracks() {
        return this.json("GET", "/tracks");
    }
    async listCases() {
        return this.json("GET", "/cases");
    }
    /**
     * GET /cases/:slug/opening — the authored opening (engagement question,
     * opening brief, beat titles and their opening narration) a client needs
     * to present a case before its first turn. Added alongside the apps/api
     * route of the same name; see that route for why it exists.
     */
    async getCaseOpening(slug) {
        return this.json("GET", `/cases/${slug}/opening`);
    }
    /**
     * Opens a case. A run already open for it is handed back (`resumed`);
     * `replaceOpen` abandons that run and starts fresh — ask first.
     */
    async createSession(caseSlug, options = {}) {
        return this.json("POST", "/sessions", { body: options.replaceOpen === true ? { caseSlug, replaceOpen: true } : { caseSlug } });
    }
    async getSession(sessionId) {
        return this.json("GET", `/sessions/${sessionId}`);
    }
    /** GET /sessions/:id/turns — the story thread: the learner's own inputs and everything that came back, per turn, from the server's event log. */
    async getSessionTurns(sessionId) {
        return this.json("GET", `/sessions/${sessionId}/turns`);
    }
    /**
     * POST /sessions/:id/turns — mandatory Idempotency-Key (auto-generated
     * unless supplied, for the retry-with-the-same-key case a real client
     * needs after a dropped connection). Returns the envelopes of this ONE
     * turn as an async iterable, in wire order.
     */
    async *sendTurn(sessionId, learnerText, options = {}) {
        const headers = { "idempotency-key": options.idempotencyKey ?? randomUUID() };
        if (options.ifMatchStateVersion !== undefined)
            headers["if-match"] = String(options.ifMatchStateVersion);
        const response = await this.request("POST", `/sessions/${sessionId}/turns`, { body: { learnerText }, headers });
        if (response.body === null)
            return;
        yield* parseSseStream(response.body);
    }
    /**
     * GET /sessions/:id/stream — reconnects automatically with exponential
     * backoff on a stream error, resuming from the last `id` actually
     * yielded (Last-Event-ID) so a reconnect never re-yields an envelope the
     * caller already saw.
     */
    async *streamSession(sessionId, options = {}) {
        let lastEventId = options.lastEventId ?? 0;
        const maxAttempts = options.maxReconnectAttempts ?? 5;
        let attempt = 0;
        while (true) {
            // A hard timeout on ESTABLISHING the connection (not on how long the
            // stream may stay open afterward — this only guards the initial
            // fetch() call) — without it, a connection that never gets a
            // response for any reason hangs this generator's very first `next()`
            // forever, with no way for a caller to notice or recover.
            const connectSignal = options.signal !== undefined ? AbortSignal.any([options.signal, AbortSignal.timeout(15_000)]) : AbortSignal.timeout(15_000);
            const response = await this.request("GET", `/sessions/${sessionId}/stream?lastEventId=${lastEventId}`, {
                headers: { "last-event-id": String(lastEventId) },
                signal: connectSignal,
            });
            if (response.body === null)
                return;
            try {
                for await (const envelope of parseSseStream(response.body)) {
                    lastEventId = envelope.id;
                    attempt = 0;
                    yield envelope;
                }
                return;
            }
            catch (error) {
                attempt += 1;
                if (attempt > maxAttempts || options.signal?.aborted === true)
                    throw error;
                await new Promise((resolve) => setTimeout(resolve, Math.min(1000 * 2 ** attempt, 10_000)));
            }
        }
    }
    async listDocuments(sessionId) {
        return this.json("GET", `/sessions/${sessionId}/documents`);
    }
    /** The desk: every delivered document with its card face. Listing is not an open (ADR 0008). */
    /** GET /sessions/:id/book: a finished engagement read back as a book (2026-09-24); 409 until the debrief exists. */
    async getCasebook(sessionId) {
        return this.json("GET", `/sessions/${sessionId}/book`);
    }
    /**
     * GET /me/arrival: how this learner and the coach met, assembled by code
     * into the record's first book (2026-10-11). 404 while there is no confirmed
     * intake to assemble it from, and again once the coach's data is removed.
     */
    async getArrivalBook() {
        return this.json("GET", "/me/arrival");
    }
    async getDocuments(sessionId) {
        return this.json("GET", `/sessions/${sessionId}/documents`);
    }
    /** The full render plan. Fetching it IS the document open the lead system keys off (ADR 0008). */
    async getDocument(sessionId, documentId) {
        return this.json("GET", `/sessions/${sessionId}/documents/${documentId}`);
    }
    async submitRecommendation(sessionId, text) {
        return this.json("POST", `/sessions/${sessionId}/recommendation`, { body: { text } });
    }
    async getDebrief(sessionId) {
        return this.json("GET", `/sessions/${sessionId}/debrief`);
    }
    async getAudit(sessionId) {
        return this.json("GET", `/sessions/${sessionId}/audit`);
    }
    async getProgression() {
        return this.json("GET", "/me/progression");
    }
    async getRubricHistory() {
        return this.json("GET", "/me/rubric-history");
    }
    /** The progression layer's read surface (prompt 10). Everything in it is engine-derived; nothing in it can express a rank. */
    async getPracticeRecord() {
        return this.json("GET", "/me/practice-record");
    }
    /** Writes (or rewrites) today's reflection. Returns the streak so the surface can update without a second fetch. */
    async submitReflection(answer) {
        return this.json("POST", "/me/reflections", { body: { answer } });
    }
    /** The six skills, explained: a static reference served from the engine's own data. */
    async getSkills() {
        return this.json("GET", "/skills");
    }
    /** The coach's front door: the welcome it speaks first and the cards it pushes. Cached server-side against the record it was written over. */
    async getCoachOpening() {
        return this.json("GET", "/coach/opening");
    }
    /** The conversation with the coach as it was kept, oldest first. */
    async getCoachThread() {
        return this.json("GET", "/coach/messages");
    }
    async sendCoachMessage(message) {
        // The server resolves WHO from the session cookie; a client-supplied id
        // could only ever disagree with it or agree with it.
        return this.json("POST", "/coach/messages", { body: { message } });
    }
    /** The exercise in progress and the briefs on offer (coach plan steps 6 to 8). */
    async getPractice() {
        return this.json("GET", "/practice");
    }
    async offerPractice(body = {}) {
        return this.json("POST", "/practice/offers", { body });
    }
    /** accept, decline, pause, resume, conclude, hint, example, variation, revision: one event each, checked by the engine on the server. */
    async practiceAction(exerciseId, action) {
        return this.json("POST", `/practice/${exerciseId}/${action}`, { body: {} });
    }
    /** The attempt carries its own key so a retry returns the attempt already recorded. */
    async practiceAttempt(exerciseId, body) {
        return this.json("POST", `/practice/${exerciseId}/attempts`, { body });
    }
    /** The practice focus (coach plan step 9): the one in play and the history. */
    async getFocus() {
        return this.json("GET", "/focus");
    }
    async suggestFocus(body = {}) {
        return this.json("POST", "/focus/suggest", { body });
    }
    async chooseFocus(focusId, statement) {
        return this.json("POST", `/focus/${focusId}/choose`, { body: statement === undefined ? {} : { statement } });
    }
    async focusAction(focusId, action) {
        return this.json("POST", `/focus/${focusId}/${action}`, { body: {} });
    }
    async focusSelfReport(focusId, text, context) {
        return this.json("POST", `/focus/${focusId}/self-report`, { body: { text, context } });
    }
    /** Law 5: the whole account, including guest work, in one portable JSON copy. */
    async getAccountExport() {
        return this.json("GET", "/me/export");
    }
    /** Law 5: what the coach and practice keep about the learner, whole; and its removal. */
    async exportCoachData() {
        return this.json("GET", "/me/coach-data");
    }
    async deleteCoachData() {
        return this.json("DELETE", "/me/coach-data");
    }
    /**
     * SELF-SERVE ACCOUNT ERASURE (docs/ACCOUNT-ERASURE-DESIGN-2026-10-20.md).
     * A signed-in account only: a guest is refused with 409. While a deletion
     * is pending these three still answer, and every other route refuses the
     * account with 403 ACCOUNT_ERASURE_PENDING.
     */
    async getErasure() {
        return this.json("GET", "/me/erasure");
    }
    /**
     * Asks for the account to be deleted 30 days from now. The server signs the
     * account out everywhere before it answers, so the caller runs its own
     * logOut() next. `emailQueued` means the confirmation is on its way, never
     * that it was sent.
     */
    async requestErasure(choices) {
        return this.json("POST", "/me/erasure", { body: choices });
    }
    /** "keep my account.": cancels a pending deletion; `cancelled` is false when nothing was pending. */
    async cancelErasure() {
        return this.json("DELETE", "/me/erasure");
    }
    /** The case library's per-learner state: the saved box, the plan acknowledgement, the unlock, and open engagements. */
    async getLibrary() {
        return this.json("GET", "/me/library");
    }
    /** Replaces the saved box whole, in the learner's order. */
    async setSavedCases(slugs) {
        return this.json("PUT", "/me/library/saved", { body: { slugs: [...slugs] } });
    }
    /** Marks the first development plan as read. Idempotent: the first date stands. */
    async acknowledgePlan() {
        return this.json("POST", "/me/library/plan-acknowledged", { body: {} });
    }
    async getSettings() {
        return this.json("GET", "/settings");
    }
    /** Names only what changes; the server keeps the rest. */
    async patchSettings(patch) {
        return this.json("PATCH", "/settings", { body: patch });
    }
    /** The seven update emails (2026-10-07): `eligible: false` until the first Harborlight report. */
    async getEmailChoices() {
        return this.json("GET", "/me/email-choices");
    }
    /** Saved whole; "no emails." is every purpose off. */
    async putEmailChoices(choices) {
        return this.json("PUT", "/me/email-choices", { body: choices });
    }
    /**
     * THE LINKS AN EMAIL CARRIES (CME stage 3b): public, token-only, no cookie
     * needed. The token is the one in the link's `?token=`; the server verifies
     * it and refuses anything it did not sign (400).
     */
    async getCheckin(token) {
        return this.json("GET", `/check-in/${encodeURIComponent(token)}`);
    }
    /** One answer to each of the four questions; 409 once answered or when the check-in consent was withdrawn. */
    async postCheckin(token, answers) {
        return this.json("POST", `/check-in/${encodeURIComponent(token)}`, { body: answers });
    }
    /** Stops the token's purpose; 200 whether or not anything changed. A POST, so no prefetch can do it. */
    async postUnsubscribe(token) {
        return this.json("POST", `/u/${encodeURIComponent(token)}`);
    }
    /**
     * The same link's stop-all (2026-10-21): every email choice off for the account the token
     * names, whichever kind of email it came from. The same answer whether or not anything
     * changed, and a POST for the same reason.
     */
    async unsubscribeAll(token) {
        return this.json("POST", `/u/${encodeURIComponent(token)}/all`);
    }
    /**
     * The voice surface (ADR 0011). Capabilities say which halves this
     * deployment has wired; a client shows "not yet." for the rest.
     */
    async getVoiceCapabilities() {
        return this.json("GET", "/voice");
    }
    /**
     * One dictation, as the container the recorder produced. Bypasses the JSON
     * helper because the body is the recording itself. `durationSeconds` is what
     * the recorder measured, sent as a hint for cost telemetry only.
     */
    async transcribe(audio, options = {}) {
        const response = await fetch(`${this.config.baseUrl}/voice/transcriptions`, {
            method: "POST",
            credentials: "include",
            headers: {
                "content-type": audio.type.length > 0 ? audio.type : "audio/webm",
                ...(options.durationSeconds !== undefined ? { "x-dictation-seconds": String(Math.round(options.durationSeconds)) } : {}),
                ...this.identityHeaders(),
            },
            body: audio,
        });
        this.captureSessionCookie(response);
        await this.told(throwIfProblem(response));
        return (await response.json());
    }
    /** The narration of one turn of this session, voiced. Addressed by turn, never by text — the server reads the prose from its own event log. */
    async narrationAudio(sessionId, turn) {
        const response = await this.request("POST", `/sessions/${sessionId}/speech`, { body: { turn } });
        return response.blob();
    }
    async healthz() {
        return this.json("GET", "/healthz");
    }
    async readyz() {
        return this.json("GET", "/readyz");
    }
}
export function createClient(config) {
    return new PushbackClient(config);
}
//# sourceMappingURL=client.js.map